import { clsx } from 'clsx';
import { type ComponentPropsWithoutRef } from 'react';
import { BadgeV2 } from '../badge-v2/badge-v2.tsx';
import {
  CardV2,
  CardV2Body,
  CardV2Description,
  CardV2Footer,
  CardV2Header,
  CardV2Title,
} from '../card-v2/card-v2.tsx';
import { FollowersV2 } from '../followers-v2/followers-v2.tsx';
import { HandleV2, type HandleV2Props } from '../handle-v2/handle-v2.tsx';
import { MoreHorizIcon } from '../icons/react/more-horiz.tsx';
import { ShareAndroidIcon } from '../icons/react/share-android.tsx';
import {
  PriceTagV2,
  type PriceTagV2Price,
} from '../price-tag-v2/price-tag-v2.tsx';
import { StreamCardV2View } from '../stream-card-v2/stream-card-v2.tsx';
import {
  streamPackageCardV2,
  streamPackageCardV2Body,
  streamPackageCardV2Cover,
  streamPackageCardV2CoverImage,
  streamPackageCardV2Description,
  streamPackageCardV2Meta,
  streamPackageCardV2MoreIcon,
  streamPackageCardV2MoreLabel,
  streamPackageCardV2StreamGrid,
  streamPackageCardV2StreamIcon,
  streamPackageCardV2StreamName,
  streamPackageCardV2StreamRow,
  streamPackageCardV2Streams,
  streamPackageCardV2Tags,
  streamPackageCardV2Title,
} from './stream-package-card-v2.css.ts';

export const StreamPackageCardV2 = ({
  className,
  ...rest
}: ComponentPropsWithoutRef<'div'>) => (
  <CardV2
    className={clsx(streamPackageCardV2, className)}
    {...rest}
    hoverable
    data-testid="stream-package-card-v2"
  />
);

export const StreamPackageCardV2Cover = ({
  src,
  className,
}: {
  src: string;
  className?: string;
}) => (
  <div className={clsx(streamPackageCardV2Cover, className)}>
    <img
      className={streamPackageCardV2CoverImage}
      src={src}
      alt=""
      data-testid="stream-package-card-cover-v2"
    />
  </div>
);

const streamCountLabel = (streamCount: number): string =>
  `${streamCount} ${streamCount === 1 ? 'stream' : 'streams'}`;

export const StreamPackageCardV2Header = ({
  streamCount,
  price,
  className,
}: {
  streamCount: number;
  price: PriceTagV2Price;
  className?: string;
}) => (
  <CardV2Header className={className}>
    <div className={streamPackageCardV2Tags}>
      <BadgeV2 tone="package">Package</BadgeV2>
      <BadgeV2 tone="tag">{streamCountLabel(streamCount)}</BadgeV2>
    </div>
    <PriceTagV2 price={price} />
  </CardV2Header>
);

export const StreamPackageCardV2Body = ({
  title,
  description,
  className,
}: {
  title: string;
  description?: string | null;
  className?: string;
}) => (
  <CardV2Body className={clsx(streamPackageCardV2Body, className)}>
    <CardV2Title className={streamPackageCardV2Title}>{title}</CardV2Title>
    {description ? (
      <CardV2Description className={streamPackageCardV2Description}>
        {description}
      </CardV2Description>
    ) : null}
  </CardV2Body>
);

export const StreamPackageCardV2Streams = ({
  streams,
  remaining,
  className,
}: {
  streams: readonly { id: string; name: string }[];
  remaining: number;
  className?: string;
}) => (
  <div
    className={clsx(streamPackageCardV2Streams, className)}
    data-testid="stream-package-card-streams-v2"
  >
    <ul className={streamPackageCardV2StreamGrid}>
      {streams.map((stream) => (
        <li key={stream.id} className={streamPackageCardV2StreamRow}>
          <span className={streamPackageCardV2StreamIcon}>
            <ShareAndroidIcon />
          </span>
          <span className={streamPackageCardV2StreamName}>{stream.name}</span>
        </li>
      ))}
    </ul>
    {remaining > 0 ? (
      <div className={streamPackageCardV2StreamRow}>
        <span className={streamPackageCardV2MoreIcon}>
          <MoreHorizIcon />
        </span>
        <span className={streamPackageCardV2MoreLabel}>
          {`+${remaining} more streams inside`}
        </span>
      </div>
    ) : null}
  </div>
);

export type StreamPackageCardV2FooterProps = {
  followers: string;
  handle: string;
  avatarSrc?: string | null;
  renderHandle?: HandleV2Props['render'];
  className?: string;
};

export const StreamPackageCardV2Footer = ({
  followers,
  handle,
  avatarSrc,
  renderHandle,
  className,
}: StreamPackageCardV2FooterProps) => (
  <CardV2Footer className={className}>
    <HandleV2 handle={handle} avatarSrc={avatarSrc} render={renderHandle} />
    <div className={streamPackageCardV2Meta}>
      <FollowersV2 count={followers} />
      <StreamCardV2View>View package</StreamCardV2View>
    </div>
  </CardV2Footer>
);
